import React, { useState } from 'react';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Avatar } from '../common/Avatar';
import { FormView } from '../forms/FormView';
import type { Datum } from '../../api/datum';

interface CohortMember {
  userId: string;
  name: string | null;
  avatarUrl: string | null;
  role: string;
}

interface CoachFormCardProps {
  datum: Datum;
  cohortName?: string;
  members: CohortMember[];
  onEdit?: () => void;
  onDelete?: () => void;
  onNudge?: () => void;
  showCohortBadge?: boolean;
}

/**
 * CoachFormCard - Displays a form/datum with reply viewing for coaches
 *
 * Unified component for displaying datum in coach views (Dashboard, Groups).
 * Shows the form template and allows viewing user replies.
 */
export function CoachFormCard({
  datum,
  cohortName,
  members,
  onEdit,
  onDelete,
  onNudge,
  showCohortBadge = true,
}: CoachFormCardProps) {
  const [showRepliesModal, setShowRepliesModal] = useState(false);

  // Get unique reply count (one per user, latest only)
  const getReplyCount = (): number => {
    if (!datum.datumUsers) return 0;
    const uniqueUserIds = new Set(datum.datumUsers.map((du: any) => du.userId));
    return uniqueUserIds.size;
  };

  // Get users who haven't replied
  const getUsersWhoDidNotReply = (): CohortMember[] => {
    if (!datum.datumUsers) return members;
    const repliedUserIds = new Set(datum.datumUsers.map((du: any) => du.userId));
    return members.filter(member => !repliedUserIds.has(member.userId));
  };

  // Get latest datumUser per user, sorted by createdAt descending
  const getLatestReplies = () => {
    if (!datum.datumUsers) return [];

    const userMap = new Map<string, any>();
    datum.datumUsers.forEach((datumUser: any) => {
      const existing = userMap.get(datumUser.userId);
      if (!existing || datumUser.createdAt > existing.createdAt) {
        userMap.set(datumUser.userId, datumUser);
      }
    });

    return Array.from(userMap.values()).sort((a, b) => b.createdAt - a.createdAt);
  };

  const replyCount = getReplyCount();
  const nonRepliedCount = getUsersWhoDidNotReply().length;
  const body = (datum.fixedData?.body as string) || '';

  return (
    <>
      <Card>
        {/* Header */}
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 flex-wrap">
            {showCohortBadge && cohortName && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {cohortName}
              </span>
            )}
            <span className="text-sm text-gray-500">
              {new Date(datum.createdAt).toLocaleDateString()}
            </span>
            {replyCount > 0 && (
              <span className="text-sm text-gray-600">
                {replyCount} {replyCount === 1 ? 'user replied' : 'users replied'}
              </span>
            )}
            {onNudge && nonRepliedCount > 0 && members.length > 0 && (
              <Button variant="ghost" size="sm" onClick={onNudge}>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                Nudge
              </Button>
            )}
          </div>

          <div className="flex gap-1">
            {onEdit && (
              <button
                onClick={onEdit}
                className="p-1 text-gray-400 hover:text-gray-600"
                title="Edit"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            )}
            {onDelete && (
              <button
                onClick={onDelete}
                className="p-1 text-gray-400 hover:text-red-600"
                title="Delete"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Content - Form template (read-only, no user data) */}
        <div className="p-4">
          <FormView
            markdown={body}
            formData={{}}
            onChange={() => {}}
            readOnly={true}
            showProgress={false}
            showSaveButton={false}
          />
        </div>

        {/* Footer Actions */}
        {replyCount > 0 && (
          <div className="border-t border-gray-200 px-4 py-3 bg-gray-50">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowRepliesModal(true)}
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
              View Replies ({replyCount})
            </Button>
          </div>
        )}
      </Card>

      {/* Replies Modal */}
      {showRepliesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Replies</h2>
              <button onClick={() => setShowRepliesModal(false)} className="text-gray-500 hover:text-gray-700">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="bg-gray-50 px-6 py-4 max-h-[70vh] overflow-y-auto">
              <div className="space-y-6">
                {getLatestReplies().map((datumUser: any) => {
                  const member = members.find(m => m.userId === datumUser.userId);
                  return (
                    <div key={datumUser.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-3">
                        <Avatar src={member?.avatarUrl} name={member?.name || 'Unknown'} size="sm" />
                        <div>
                          <p className="font-medium text-gray-900">{member?.name || 'Unknown'}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(datumUser.createdAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="p-4">
                        <FormView
                          markdown={body}
                          formData={datumUser.data || {}}
                          onChange={() => {}}
                          readOnly={true}
                          showProgress={false}
                          showSaveButton={false}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
